/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @author Satyaki Bose
 * SyllabusUploadMainComponent.jsx
 * Main landing page for syllabus upload with tabs for upload/manual add
 */

import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, Tabs, Tab, Paper, Button } from '@material-ui/core';

import CustomTabPanel, { a11yProps } from './CustomTabPanel';
import SyllabusUploadCore from './SyllabusUploadCore/SyllabusUploadCore';
import AssignmentListMainComponent from './AssignmentList/AssignmentListMainComponent';
import SafeButtonWrapper from './common/SafeButtonWrapper';

import { SyllabusProvider, useSyllabus } from '../contexts/SyllabusContext';
import DateAdjusterContent from './BulkEdit/components/DateAdjuster';

import Utils from '../common/utils';
import useAetSyllabusStyles from './styles/AetSyllabusStyles';
import useOnereaderSyllabusStyles from './styles/OneReaderSyllabusStyles';
import {
  BULK_EDIT_MODE,
  DEFAULT_TAB_ORDER,
  TAB_IDS
} from '../common/constants';
import CustomScrollbar from './CustomScrollbar/CustomScrollbar';

// Inner component that uses the context
function SyllabusUploadContent(props) {
  const { handleMethodChange, onInit, onUploadSuccess } = props;
  const { state, actions } = useSyllabus();
  const classes = Utils.getSyllabusStyles(
    props.parentApp,
    { aet: useAetSyllabusStyles, onereader: useOnereaderSyllabusStyles },
    useAetSyllabusStyles
  );

  // global state from context
  const {
    uploadedFile,
    isUploading,
    tabValue,
    syllabusInfo
  } = state;

  // global actions from context

  const {
    setTab,
    onConfirmClick
  } = actions;

  // Handle configuration for different modes
  const isBulkEditMode = props.config?.mode === BULK_EDIT_MODE;
  const showTabs = isBulkEditMode ? props.config.showTabs : (props.parentApp === 'aet');
  const showTabsinBulkEdit = !!props.config?.showTabsinBulkEdit;
  const showAssignments = props.config?.showAssignments;
  const [fileUploaded, setFileUploaded] = useState(false);
  const [isFileUploading, setIsFileUploading] = useState(false);

  // Track previous syllabusInfo to detect transition from data → empty
  const prevSyllabusInfoRef = useRef(state.syllabusInfo);
  const hasInitializedRef = useRef(true);
  // Reset ONLY when transitioning from having data to empty (cleared by parent)
  useEffect(() => {
    const hadData = prevSyllabusInfoRef.current?.data?.schedule?.length > 0;
    const hasData = state.syllabusInfo?.data?.schedule?.length > 0;

    // FORCE RESET on first render
    if (hasInitializedRef.current) {
      setFileUploaded(false);
      setIsFileUploading(false);
      hasInitializedRef.current = false;
    } else if (hasData && !fileUploaded && props.forceListView) {
      // Only allow auto-switch AFTER first render
      setFileUploaded(true);
    }
    // Only reset if we HAD data before and now it's empty (was cleared)
    if (hadData && !hasData) {
      setFileUploaded(false);
      setIsFileUploading(false);
      // Reset to upload tab using dynamic index from tabOrder
      actions.setTab(actions.getTabIndexById(TAB_IDS.UPLOAD));
    }
    // Update ref for next comparison
    prevSyllabusInfoRef.current = state.syllabusInfo;
  }, [state.syllabusInfo, actions]);

  const handleUploadFileName = () => {
    setFileUploaded(true);
    onUploadSuccess();
  };

  const handleUploadingScreenChange = (value = true) => {
    setIsFileUploading(value);
  };

  // Helper to check if current tab is the specified tab by ID
  const isCurrentTab = (tabId) => {
    const tabOrder = props.tabOrder || DEFAULT_TAB_ORDER;
    const tab = tabOrder.find(t => t.id === tabId);

    return tab ? tabValue === tab.index : false;
  };

  const hasAssignmentsData = syllabusInfo &&
    syllabusInfo.data &&
    Array.isArray(syllabusInfo.data.schedule) &&
    syllabusInfo.data.schedule.length > 0;

  const hasAssignments =
    isCurrentTab(TAB_IDS.ADD_MANUALLY) &&
    syllabusInfo &&
    syllabusInfo.data &&
    Array.isArray(syllabusInfo.data.schedule) &&
    syllabusInfo.data.schedule.length > 0;

  const showConfirmBtn = (isBulkEditMode && isCurrentTab(TAB_IDS.ADD_MANUALLY));

  // Tab change handler
  const handleTabChange = (event, newValue) => {
    const tabOrder = props.tabOrder;
    const isAddManuallyTab = Utils.isTabIdAtIndex(TAB_IDS.ADD_MANUALLY, newValue, tabOrder);
    handleMethodChange(isAddManuallyTab);
    if (isBulkEditMode) {
      // For bulk edit, always allow tab changes
      setTab(newValue);
    } else if (props.parentApp === 'aet') {
      // For AET apps, normal tab switching
      setTab(newValue);
    }
    // For non-AET apps, do nothing since tabs aren't shown
  };


  // Common function to call onConfirmClick with consistent data format
  const handleConfirm = (syllabusInfoData, assignmentsData = null) => {
    const assignments = assignmentsData ||
                      syllabusInfoData?.data?.schedule ||
                      state.assignments ||
                      [];

    const combinedData = {
      syllabusInfo: syllabusInfoData,
      assignments
    };

    onConfirmClick(combinedData, fileUploaded);
  };


  // TODO:: check all scenarios?
  const handleDateAdjustApply = () => {
    try {
      // Get date adjustment parameters from context
      const params = state.dateAdjustParams || {};
      const { startDate, endDate, selectedDays, startTime, endTime } = params;

      const assignmentsSource = syllabusInfo?.data?.schedule || state.assignments || [];
      if (!assignmentsSource || assignmentsSource.length === 0) {
        // nothing to do — still call confirm to keep existing flow
        handleConfirm(syllabusInfo);

        return;
      }

      // If params are missing, fall back to direct confirm
      if (!startDate || !endDate || !Array.isArray(selectedDays) || selectedDays.length === 0) {
        handleConfirm(syllabusInfo);

        return;
      }

      // Compute updated schedule
      const updated = Utils.distributeDates(assignmentsSource, startDate, endDate, selectedDays, { startTime, endTime });

      // Persist updates to context via provided action (per-assignment)
      if (actions && typeof actions.bulkUpdateAssignments === 'function') {
        actions.bulkUpdateAssignments(updated); // Single call to set all assignments without edit callbacks
      }

      // Build updated syllabusInfo and call parent confirm callback
      const newSyllabusInfo = {
        ...syllabusInfo,
        data: {
          ...syllabusInfo?.data,
          schedule: updated
        }
      };
      handleConfirm(newSyllabusInfo);
    } catch {
      // fallback to original confirm on error
      handleConfirm(syllabusInfo);
    }
  };

  // CTA button click handler, only comes for bulk edit mode
  const handleCtaClick = () => {
    if (fileUploaded) {
      handleConfirm(syllabusInfo);
    } else {
      handleDateAdjustApply();
    }
  };

  // Get tab labels based on mode and tabOrder
  const getTabLabels = () => {
    const tabOrder = props.tabOrder || DEFAULT_TAB_ORDER;

    // Label mapping by tab ID
    const labelMap = {
      [TAB_IDS.UPLOAD]: props?.textInfo?.uploadSyllabusTabLabel,
      [TAB_IDS.ADD_MANUALLY]: props?.textInfo?.addManuallyTabLabel,
      [TAB_IDS.DATE_ADJUSTER]: props?.textInfo?.dateAdjusterTabLabel
    };

    if (isBulkEditMode) {
      // For bulk edit, add date adjuster at the end
      const bulkEditTabs = [
        ...tabOrder,
        { index: tabOrder.length, id: TAB_IDS.DATE_ADJUSTER }
      ];

      return bulkEditTabs
        .sort((a, b) => a.index - b.index)
        .map((tab, idx) => ({
          label: labelMap[tab.id],
          id: tab.id,
          props: a11yProps(idx)
        }));
    }

    // Sort by index and create tab labels
    return tabOrder
      .sort((a, b) => a.index - b.index)
      .map((tab, idx) => ({
        label: labelMap[tab.id],
        id: tab.id,
        props: a11yProps(idx)
      }));
  };

  // Get button text based on mode and tab
  const getButtonText = () => {
    if (isBulkEditMode) {
      return isCurrentTab(TAB_IDS.UPLOAD)
        ? (state.ctaLabels?.upload)
        : (state.ctaLabels?.confirm);
    }

    return (!showTabs || isCurrentTab(TAB_IDS.UPLOAD))
      ? (state.ctaLabels?.upload)
      : (state.ctaLabels?.confirm);
  };

  const tabLabels = getTabLabels();

  const isBulkEditButtonEnabled = () => {
    const params = state.dateAdjustParams || {};
    const { startDate, endDate, selectedDays } = params;
    if (startDate && endDate && selectedDays?.length > 0) {
      return true;
    }

    return false;
  };

  /**
   * Get the data attribute for the tab
   * @returns {string} - The data attribute for the tab
   */
  const getTabDataAttr = () => {
    if (isFileUploading) {
      return 'uploading';
    }

    if (fileUploaded) {
      return 'uploaded';
    }

    return 'initial';
  };

  useEffect(() => {
    const tabOrder = props.tabOrder || DEFAULT_TAB_ORDER;
    const isAddManuallyFirst = Utils.isTabIdAtIndex(TAB_IDS.ADD_MANUALLY, 0, tabOrder);
    onInit(isAddManuallyFirst);
  }, []);

  return (
    <Box
      className={classes.root}
      style={{
        height: props.rootHeight || '100%'
      }}
    >
      <CustomScrollbar enableScrollPadding={false} showScrollbar={isBulkEditMode}>
        <Box className={classes.contentWrapper}>

          {/* Show tabs for bulk edit mode always, or for AET when no assignments */}
          {showTabs && (!fileUploaded || props.showSyllabusUploadTabs) && !isFileUploading && (isBulkEditMode || !hasAssignments) && (
          <Paper className={`${classes.tabsContainer} ${isBulkEditMode && !showTabsinBulkEdit ? 'hidden' : ''}`}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="syllabus upload tabs"
              classes={{ root: classes.syllabusUploadTabs }}
            >
              {tabLabels.map((tab, index) => {
                const hideTab = isBulkEditMode && tab.label === 'Add manually';

                return (
                  <Tab
                    key={index}
                    label={tab.label}
                    {...tab.props}
                    className={`${classes.tab} ${hideTab ? classes.hideTab : ''}`}
                  />
                );
              })}
            </Tabs>
          </Paper>
          )}

          {/* Upload tab - dynamic index based on tabOrder */}
          <CustomTabPanel
            value={tabValue}
            index={Utils.getTabIndexById(TAB_IDS.UPLOAD, props.tabOrder)}
            className={classes.tabPanel}
            data-tab={getTabDataAttr()}
          >
            <SyllabusUploadCore
              textInfo={props.textInfo}
              parentApp={props.parentApp}
              useChannelsComponent={props.useChannelsComponent}
              clientInformation={props.clientInformation}
              {...props}
              onUploadFileName={handleUploadFileName}
              handleUploadingScreenChange={handleUploadingScreenChange}
              env={props.env}
              locale={props.locale}
              className={classes}
            />
          </CustomTabPanel>

          {/* Date adjuster tab (only for bulk edit) - always last */}
          {showTabs && showTabsinBulkEdit && isBulkEditMode && (
          <CustomTabPanel value={tabValue} index={Utils.getTabIndexById(TAB_IDS.DATE_ADJUSTER, props.tabOrder)} className={classes.tabPanel}>
            <DateAdjusterContent
              textInfo={props.textInfo}
              daysOfWeek={props.daysOfWeek}
            />
          </CustomTabPanel>
          )}


          {/* Assignment list tab (Add manually) - dynamic index based on tabOrder */}
          {showTabs && (
          <CustomTabPanel value={tabValue} index={Utils.getTabIndexById(TAB_IDS.ADD_MANUALLY, props.tabOrder)} className={classes.tabPanel}>
            <AssignmentListMainComponent
              parentApp={props.parentApp}
              textInfo={props.textInfo}
              tocData={props.tocData}
              chapterMcqMapping={props.chapterMcqMapping}
              onUploadComplete={props.onUploadComplete}
              assignmentFormActions={props.assignmentFormActions}
              onSaveAndAdd={props.onSaveSingleAssignment}
              showListView={fileUploaded}
              restrictAlreadyAssigned={props.restrictAlreadyAssigned}
              showCheckboxes={props.showCheckboxes}
              onDeleteReady={props.onDeleteReady}
              showPublishButton={props.showPublishButton}
              isBulkEditMode={isBulkEditMode}
              handleUploadingScreenChange={handleUploadingScreenChange}
              onValidationChange={props.onValidationChange}
              timeZoneOptions={props.timeZoneOptions}
              excludeTitles={props.excludeTitles}
              showAssignmentTitle={props.showAssignmentTitle}
              titleValidationConfig={props.titleValidationConfig}
              assignmentConfig={props.assignmentConfig}
            />
          </CustomTabPanel>
          )}

          {/* Assignments view for bulk edit - always show when assignments are present */}
          {isBulkEditMode && hasAssignmentsData && showAssignments && isCurrentTab(TAB_IDS.UPLOAD) && (
          <div style={{ marginTop: 24 }}>
            <AssignmentListMainComponent
              parentApp={props.parentApp}
              textInfo={props.textInfo}
              tocData={props.tocData}
              chapterMcqMapping={props.chapterMcqMapping}
              onUploadComplete={props.onUploadComplete}
              assignmentFormActions={props.assignmentFormActions}
              showCheckboxes={props.showCheckboxes}
              onDeleteReady={props.onDeleteReady}
              showPublishButton={props.showPublishButton}
              isBulkEditMode={isBulkEditMode}
              onValidationChange={props.onValidationChange}
              timeZoneOptions={props.timeZoneOptions}
              excludeTitles={props.excludeTitles}
              showAssignmentTitle={props.showAssignmentTitle}
              titleValidationConfig={props.titleValidationConfig}
              assignmentConfig={props.assignmentConfig}
            />
          </div>
          )}
        </Box>
      </CustomScrollbar>
      {((!props.useChannelsComponent && !isUploading) || (props.useChannelsComponent && showConfirmBtn)) && (
      <Box className={`${classes.ctaSection} ${!fileUploaded ? classes.hideConfirmButton : ''}`}>
        <SafeButtonWrapper isLoading={props.isConfirmPublishInProgress}>
          <Button
            id="syllabus-cta-btn"
            variant="contained"
            className={
                `${classes.uploadButton} ${isBulkEditMode ? classes.bulkEditCtaButton : ''}`}
            disabled={
                isBulkEditMode && !isCurrentTab(TAB_IDS.ADD_MANUALLY)
                  ? !isBulkEditButtonEnabled()
                  : ((!showTabs || isCurrentTab(TAB_IDS.UPLOAD)) && !uploadedFile) ||
      (isCurrentTab(TAB_IDS.ADD_MANUALLY) && (!state.assignments || state.assignments.length === 0))
}
            onClick={handleCtaClick}
          >
            {getButtonText()}
          </Button>
        </SafeButtonWrapper>
      </Box>
      )}
    </Box>
  );
}

SyllabusUploadContent.propTypes = {
  progressTitle: PropTypes.string,
  progressDesc: PropTypes.string,
  dragText: PropTypes.string,
  uploadTitle: PropTypes.string,
  uploadDesc: PropTypes.string,
  fileErrorText: PropTypes.string,
  orText: PropTypes.string,
  browseLabel: PropTypes.string,
  parentApp: PropTypes.string,
  clientInformation: PropTypes.object,
  handleMethodChange: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  onInit: PropTypes.func,
  textInfo: PropTypes.shape({
    dragText: PropTypes.string,
    allowedExts: PropTypes.array,
    orText: PropTypes.string,
    browseLabel: PropTypes.string,
    uploadLabel: PropTypes.string,
    fileErrorText: PropTypes.string,
    allowedTypes: PropTypes.array,
    bulkEditSubtitle: PropTypes.string,
    bulkEditTitle: PropTypes.string,
    addEditLaterText: PropTypes.string,
    uploadSyllabusTabLabel: PropTypes.string,
    dateAdjusterTabLabel: PropTypes.string,
    addManuallyTabLabel: PropTypes.string,
    assignmentsDueDays: PropTypes.string,
    bulkSplitInstruction: PropTypes.string,
    bulkEditDescription: PropTypes.string,
    firstAssignmentDue: PropTypes.string,
    lastAssignmentDue: PropTypes.string
  }),
  config: PropTypes.object,
  tocData: PropTypes.array.isRequired,
  useChannelsComponent: PropTypes.bool,
  chapterMcqMapping: PropTypes.object,
  onUploadComplete: PropTypes.func,
  assignmentFormActions: PropTypes.object,
  onSaveSingleAssignment: PropTypes.func,
  daysOfWeek: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  restrictAlreadyAssigned: PropTypes.bool,
  rootHeight: PropTypes.string,
  showCheckboxes: PropTypes.bool,
  onDeleteReady: PropTypes.func,
  showPublishButton: PropTypes.bool,
  renderInModal: PropTypes.bool,
  showSyllabusUploadTabs: PropTypes.bool,
  onValidationChange: PropTypes.func,
  env: PropTypes.string,
  locale: PropTypes.string,
  isConfirmPublishInProgress: PropTypes.bool,
  forceListView: PropTypes.bool,
  tabOrder: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      id: PropTypes.string.isRequired
    })
  ),
  syllabusDateCallbacks: PropTypes.shape({
    normalizeResponseDates: PropTypes.func
  }),
  timeZoneOptions: PropTypes.array,
  showAssignmentTitle: PropTypes.bool,
  excludeTitles: PropTypes.array,
  titleValidationConfig: PropTypes.object,
  assignmentConfig: PropTypes.object
};

SyllabusUploadContent.defaultProps = {
  renderInModal: false,
  progressTitle: '',
  progressDesc: '',
  dragText: '',
  uploadTitle: '',
  uploadDesc: '',
  fileErrorText: '',
  orText: '',
  browseLabel: '',
  textInfo: {},
  parentApp: 'aet',
  clientInformation: {},
  config: {},
  useChannelsComponent: true,
  chapterMcqMapping: null,
  handleMethodChange: () => {},
  onUploadSuccess: () => {},
  onInit: () => {},
  onUploadComplete: () => {},
  assignmentFormActions: {},
  onSaveSingleAssignment: () => {},
  daysOfWeek: [
    { key: 'M', label: 'Monday' },
    { key: 'T', label: 'Tuesday' },
    { key: 'W', label: 'Wednesday' },
    { key: 'Th', label: 'Thursday' },
    { key: 'F', label: 'Friday' },
    { key: 'S', label: 'Saturday' },
    { key: 'Su', label: 'Sunday' }
  ],
  restrictAlreadyAssigned: true,
  rootHeight: '100%',
  showCheckboxes: false,
  showPublishButton: false,
  onDeleteReady: () => {},
  showSyllabusUploadTabs: false,
  onValidationChange: () => {},
  env: '',
  locale: '',
  isConfirmPublishInProgress: false,
  forceListView: false,
  tabOrder: DEFAULT_TAB_ORDER,
  timeZoneOptions: [],
  showAssignmentTitle: false,
  excludeTitles: [],
  titleValidationConfig: {},
  syllabusDateCallbacks: {},
  assignmentConfig: {}
};

/**
 * SyllabusUploadMainComponent: Main component with tabs for uploading syllabus
 * Now uses Context API for state management
 */
export default function SyllabusUploadMainComponent(props) {
  return (
    <SyllabusProvider
      clientInformation={props.clientInformation}
      onUploadComplete={props.onUploadComplete}
      onFileError={props.onFileError}
      onAddNewClick={props.onAddNewClick}
      onEditClick={props.onEditClick}
      onConfirmClick={props.onConfirmClick}
      initialSyllabusInfo={props.initialSyllabusInfo}
      runCoverageDedupeOnSync={props.runCoverageDedupeOnSync}
      currentAssignedList={props.currentAssignedList}
      ctaLabels={props.ctaLabels}
      parentApp={props.parentApp}
      config={props.config}
      tocData={props.tocData}
      restrictAlreadyAssigned={props.restrictAlreadyAssigned}
      assignmentDefaults={props.assignmentDefaults}
      onAssignmentDefaultsChange={props.onAssignmentDefaultsChange}
      tabOrder={props.tabOrder}
      syllabusDateCallbacks={props.syllabusDateCallbacks}
    >
      <SyllabusUploadContent {...props} />
    </SyllabusProvider>
  );
}

// PropTypes remain the same
SyllabusUploadMainComponent.propTypes = {
  clientInformation: PropTypes.object.isRequired,
  onUploadComplete: PropTypes.func.isRequired,
  onAddNewClick: PropTypes.func,
  onEditClick: PropTypes.func,
  onConfirmClick: PropTypes.func.isRequired,
  onFileError: PropTypes.func,
  progressTitle: PropTypes.string,
  progressDesc: PropTypes.string,
  parentApp: PropTypes.string,
  uploadTitle: PropTypes.string,
  uploadDesc: PropTypes.string,
  initialSyllabusInfo: PropTypes.object,
  runCoverageDedupeOnSync: PropTypes.number,
  ctaLabels: PropTypes.object,
  currentAssignedList: PropTypes.array,
  handleMethodChange: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  onInit: PropTypes.func,
  textInfo: PropTypes.shape({
    dragText: PropTypes.string,
    allowedExts: PropTypes.array,
    orText: PropTypes.string,
    browseLabel: PropTypes.string,
    uploadLabel: PropTypes.string,
    fileErrorText: PropTypes.string,
    allowedTypes: PropTypes.array,
    addEditLaterText: PropTypes.string,
    assignmentsDueDays: PropTypes.string,
    bulkSplitInstruction: PropTypes.string,
    bulkEditDescription: PropTypes.string,
    firstAssignmentDue: PropTypes.string,
    lastAssignmentDue: PropTypes.string
  }),
  config: PropTypes.shape({
    showTabs: PropTypes.bool,
    showAssignments: PropTypes.bool,
    mode: PropTypes.string // New prop to switch modes
  }),
  assignmentFormActions: PropTypes.shape({
    save: PropTypes.string,
    saveAndAdd: PropTypes.string,
    delete: PropTypes.string,
    saveAndClose: PropTypes.string
  }),
  daysOfWeek: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  rootHeight: PropTypes.string,
  tocData: PropTypes.array,
  restrictAlreadyAssigned: PropTypes.bool,
  locale: PropTypes.string,
  env: PropTypes.string,
  assignmentDefaults: PropTypes.shape({
    date: PropTypes.string,
    time: PropTypes.string
  }),
  onAssignmentDefaultsChange: PropTypes.func,
  tabOrder: PropTypes.arrayOf(
    PropTypes.shape({
      index: PropTypes.number.isRequired,
      id: PropTypes.string.isRequired
    })
  ),
  syllabusDateCallbacks: PropTypes.shape({
    normalizeResponseDates: PropTypes.func
  }),
  assignmentConfig: PropTypes.object
};

SyllabusUploadMainComponent.defaultProps = {
  onFileError: () => {},
  onAddNewClick: () => {},
  onEditClick: () => {},
  handleMethodChange: () => {},
  onUploadSuccess: () => {},
  onInit: () => {},
  uploadTitle: 'Upload your syllabus',
  uploadDesc: 'Drag and drop your syllabus file here or click to browse.',
  progressTitle: 'Analyzing your syllabus...',
  progressDesc: 'Please wait while we process your syllabus.',
  parentApp: 'aet',
  initialSyllabusInfo: null,
  runCoverageDedupeOnSync: 0,
  currentAssignedList: null,
  textInfo: {
    addEditLaterText: ''
  },
  ctaLabels: {
  },
  config: {
    showTabs: true,
    showAssignments: true
  },
  assignmentFormActions: {},
  daysOfWeek: [
    { key: 'M', label: 'Monday' },
    { key: 'T', label: 'Tuesday' },
    { key: 'W', label: 'Wednesday' },
    { key: 'Th', label: 'Thursday' },
    { key: 'F', label: 'Friday' },
    { key: 'S', label: 'Saturday' },
    { key: 'Su', label: 'Sunday' }
  ],
  rootHeight: '100%',
  tocData: [],
  restrictAlreadyAssigned: true,
  env: '',
  locale: '',
  assignmentDefaults: null,
  onAssignmentDefaultsChange: () => {},
  tabOrder: DEFAULT_TAB_ORDER,
  syllabusDateCallbacks: {},
  assignmentConfig: {}
};
